import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
  ListItem,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { MdWarningAmber } from "react-icons/md";
import { TbBuildingWarehouse } from "react-icons/tb";

import type { OrderReadiness } from "../types/profitOrderReadiness";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  /** Órdenes que se enviarían, con lo que les falta a cada una. */
  candidates: OrderReadiness[];
};

/**
 * Confirmación de la carga en Profit.
 *
 * Existe porque el envío **no tiene vuelta atrás**: el datamart no expone
 * `cancelPurchasingOrder`, así que una orden mandada por error se anula a mano
 * en el ERP. También es donde se ve qué le falta a cada orden — antes esto se
 * descubría en la respuesta del `POST`, como un 422 sin pistas.
 */
export default function ProfitPushConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  candidates,
}: Props) {
  const surface = useColorModeValue("white", "navy.800");
  const muted = useColorModeValue("gray.600", "gray.400");
  const rowBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const ready = candidates.filter((c) => c.issues.length === 0);
  const blocked = candidates.filter((c) => c.issues.length > 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="lg"
      scrollBehavior="inside"
      closeOnOverlayClick={!isSubmitting}
    >
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(8px)" />
      <ModalContent bg={surface} borderRadius="20px">
        <ModalHeader pb="4px">
          <Flex align="center" gap="10px">
            <Icon as={TbBuildingWarehouse} w="24px" h="24px" color="teal.400" />
            <Text fontSize="lg" fontWeight="700">
              Cargar en Profit
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />

        <ModalBody>
          <Flex
            align="flex-start"
            gap="10px"
            bg="orange.50"
            _dark={{ bg: "whiteAlpha.100" }}
            borderRadius="14px"
            p="12px"
            mb="16px"
          >
            <Icon
              as={MdWarningAmber}
              color="orange.400"
              w="20px"
              h="20px"
              mt="2px"
            />
            <Text fontSize="sm" color={muted}>
              Se crearán <b>órdenes de compra reales</b> en el ERP.{" "}
              <b>No se pueden anular desde la aplicación</b>: cualquier
              corrección es manual en Profit.
            </Text>
          </Flex>

          {ready.length > 0 ? (
            <Box mb={blocked.length > 0 ? "16px" : 0}>
              <Text fontSize="sm" fontWeight="600" mb="8px">
                Se enviarán {ready.length} orden(es)
              </Text>
              <VStack align="stretch" spacing="6px">
                {ready.map(({ order, lineCount }) => (
                  <Flex
                    key={String(order.id ?? order.number)}
                    bg={rowBg}
                    borderRadius="12px"
                    px="12px"
                    py="8px"
                    justify="space-between"
                    align="center"
                  >
                    <Text fontSize="sm" fontWeight="500">
                      #{order.number}
                    </Text>
                    <Flex gap="8px" align="center">
                      <Badge colorScheme="teal" fontSize="10px">
                        {order.vendor_code}
                      </Badge>
                      <Text fontSize="xs" color={muted}>
                        {lineCount} renglón(es)
                      </Text>
                    </Flex>
                  </Flex>
                ))}
              </VStack>
            </Box>
          ) : null}

          {blocked.length > 0 ? (
            <Box>
              {ready.length > 0 ? <Divider mb="16px" /> : null}
              <Text fontSize="sm" fontWeight="600" mb="4px">
                {blocked.length} orden(es) no se pueden enviar
              </Text>
              <Text fontSize="xs" color={muted} mb="8px">
                Prendé el interruptor «Datos de compra (Profit)» de cada orden
                para completar lo que falta.
              </Text>
              <VStack align="stretch" spacing="8px">
                {blocked.map(({ order, issues }) => (
                  <Box
                    key={String(order.id ?? order.number)}
                    bg={rowBg}
                    borderRadius="12px"
                    px="12px"
                    py="8px"
                    borderLeftWidth="3px"
                    borderLeftColor="orange.400"
                  >
                    <Text fontSize="sm" fontWeight="500" mb="4px">
                      #{order.number}
                    </Text>
                    <UnorderedList spacing="2px" ms="16px">
                      {issues.map((issue) => (
                        <ListItem key={issue} fontSize="xs" color={muted}>
                          {issue}
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Box>
                ))}
              </VStack>
            </Box>
          ) : null}
        </ModalBody>

        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            colorScheme="teal"
            onClick={onConfirm}
            isLoading={isSubmitting}
            loadingText="Cargando"
            // Sin ninguna orden completa no hay nada que enviar; habilitarlo
            // sólo produciría un 422 por cada una.
            isDisabled={ready.length === 0}
          >
            {ready.length > 0
              ? `Cargar ${ready.length} orden(es)`
              : "Nada para cargar"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
